import React, { useEffect, useState } from "react";

//date picker
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Dropdown from "../../../Common/Dropdown";

// utils
import areObjectsEqual from "../../../../utils/areObjectsEqual";

//Childs
import { convertLocalToUTCDate } from "../../../../utils";
import {
  getBackgrounds
} from "../../../../utils/services";

const Form = ({ formData, handleSubmit, onDiscard, artPack, setSpinner, campaign }) => {
  const [form, setForm] = useState(formData);
  /* eslint-disable no-unused-vars */
  const [copyData, setCopyData] = useState(formData);
  const [conditionalPattern, setConditionalPattern] = useState("");
  const [conditionalLayer, setConditionalLayer] = useState("");
  const hasUnsavedChanges = areObjectsEqual(copyData, form);

  const isNew = !form.id;
  const category = ["background", "base image", "layer", "layerchoice"];
  const [dropdownList, setDropdownList] = useState([]);
  const [patternList, setPatternList] = useState([]);
  const [layerList, setLayerList] = useState([]);
  const [layerChoiceList, setLayerChoiceList] = useState([]);

  //Dates
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  //errors
  const [errorStartsOn, setErrorStartsOn] = useState(false);
  const [errorEndsOn, setErrorEndsOn] = useState(false);
  const [errorMaxCount, setErrorMaxCount] = useState(false);
  const [errorRarityType, setErrorRarityType] = useState(false);

  const getImage = async (name) => {
    if (!name) {
      console.warn("No name provided for getImage.");
      return null;
    }

    try {
      const req = await fetch(`/api/file/name/${name}`);
      if (!req.ok) {
        throw new Error(`Failed to fetch image. Status: ${req.status}`);
      }

      const data = await req.json();

      if (!data?.image?.data) {
        throw new Error("Invalid image data received.");
      }

      const uint8Array = new Uint8Array(data.image.data);
      let binaryString = "";
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }

      return `data:${data.type};base64,${btoa(binaryString)}`;
    } catch (error) {
      console.error(`Error in getImage for ${name}:`, error.message);
      return null; // Return null or fallback image URL
    }
  };

  const getBackgroundList = async () => {
    const res = await getBackgrounds(campaign?.campaign_id, artPack?.art_pack_id);
    const data = res.map((el) => {
      return {
        id: el.background_id,
        rank: el.rank,
        name: el.color_hex_code,
        price: el.price ? el.price : 0
      };
    });
    return data;
  };

  const getPatternList = async () => {
    const res = await fetch(`/api/artpack/${artPack?.art_pack_id}/pattern`);
    const json = await res.json();
    const data = await Promise.all(
      json.map(async (el) => {
        const previewImage =
          el.thumbnail_url.startsWith("http") ||
          el.thumbnail_url.startsWith("https")
            ? el.thumbnail_url
            : await getImage(el.thumbnail_url);
        return {
          id: el.pattern_id,
          name: el.pattern_name,
          preview: previewImage,
        };
      })
    );
    return data;
  };

  const getLayerList = async (patternId) => {
    const res = await fetch(`/api/pattern/${patternId}/layer`);
    const json = await res.json();
    const data = json.map((el) => {
      return {
        id: el.layer_id,
        name: el.layer_name,
      };
    });
    return data;
  };

  const getLayerChoiceList = async (layerId) => {
    const res = await fetch(`/api/layer/${layerId}/layerchoice`);
    const json = await res.json();
    const data = await Promise.all(
      json.map(async (el) => {
        const previewImage =
          el.layer_preview_url.startsWith("http") ||
          el.layer_preview_url.startsWith("https")
            ? el.layer_preview_url
            : await getImage(el.layer_preview_url);
        return {
          id: el.layer_choice_id,
          preview: previewImage,
        };
      })
    );
    return data;
  };

  const getApprovedNameList = async () => {
    const res = await fetch(`/api/artpack/${artPack?.art_pack_id}/approvedname`);
    const json = await res.json();
    const data = json.map((el) => {
      return {
        id: el.approved_name_id,
        name: el.approved_name,
      };
    });
    return data;
  };

  const loadRelativeDropdownList = async () => {
    let dropdownList = [];
    setSpinner(true);
    switch (form.rarity_type) {
      case category[0]:
        dropdownList = await getBackgroundList();
        setDropdownList(dropdownList);
        break;
      case category[1]:
        dropdownList = await getPatternList();
        setDropdownList(dropdownList);
        break;
      case category[2]:
      case category[3]:
      case category[4]:
      case category[5]:
        setPatternList(await getPatternList());
        setLayerList([]);
        setLayerChoiceList([]);
        break;
      case category[6]:
        dropdownList = await getApprovedNameList();
        setDropdownList(dropdownList);
        break;
      default:
        dropdownList = [];
        setDropdownList(dropdownList);
    }

    setSpinner(false);
  };
  //set background from artist
  useEffect(() => {
    setDropdownList([]);
    if (form.starts_on) setStartDate(new Date(form.starts_on));
    if (form.ends_on) setEndDate(new Date(form.ends_on));
    loadRelativeDropdownList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.rarity_type]);

  useEffect(() => {
    const getData = async () => {
      if (!isNew) {
        if (form.rarity_type === "layer") {
          const layerRes = await fetch(`/api/layer/${form.id_of_type}`);
          const layerData = await layerRes.json();
          const patternRes = await fetch(
            `/api/pattern/${layerData.pattern_id}`
          );
          const patternData = await patternRes.json();
          setConditionalPattern(patternData.pattern_name);
          const data = await getLayerList(patternData.pattern_id);
          setDropdownList(data);
          setLayerList(data);
        }
        if (form.rarity_type === "layerchoice") {
          const layerChoiceRes = await fetch(
            `/api/layerchoice/${form.id_of_type}`
          );
          const layerChoiceData = await layerChoiceRes.json();
          const layerRes = await fetch(
            `/api/layer/${layerChoiceData.layer_id}`
          );
          const layerData = await layerRes.json();
          setConditionalLayer(layerData.layer_name);
          const layerChoiceList = await getLayerChoiceList(layerData.layer_id);
          setDropdownList(layerChoiceList);
          setLayerChoiceList(layerChoiceList);
          const patternRes = await fetch(
            `/api/pattern/${layerData.pattern_id}`
          );
          const patternData = await patternRes.json();
          setConditionalPattern(patternData.pattern_name);
          const layerList = await getLayerList(patternData.pattern_id);
          setLayerList(layerList);
        }
      }
    };
    getData();
  }, [form.rarity_type, form.id_of_type, getLayerChoiceList, isNew]);

  const handleChange = (e) => {
    let value;

    if (e.target.name === "max_count") {
      value = Math.max(0, Math.min(500, Number(e.target.value)));
    } else {
      value = e.target.value;
    }

    setForm({ ...form, [e.target.name]: value });
  };

  const handleDropdownChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  const handleClick = () => {
    setErrorStartsOn(false);
    setErrorEndsOn(false);
    setErrorMaxCount(false);
    setErrorRarityType(false);

    if (form.starts_on && form.ends_on && form.max_count && form.rarity_type) {
      //send the new data to te father component
      handleSubmit(form);
    } else {
      if (!form.starts_on) setErrorStartsOn(true);
      if (!form.ends_on) setErrorEndsOn(true);
      if (!form.max_count) setErrorMaxCount(true);
      if (!form.rarity_type) setErrorRarityType(true);
    }
  };

  const conditionalPatternList = () => {
    const whichCategory = category.indexOf(form.rarity_type);
    if (whichCategory > 1 && whichCategory < 6) {
      return (
        <div className="row col-sm-12">
          <div className="form-labels mt-4">Base Image</div>
          {isNew && (
            <Dropdown
              category="rarity"
              categorySubType="base image"
              dropdownList={patternList}
              dropdownHeader={"Select a Base Image"}
              handleChange={async (name, patternId) => {
                const data = await getLayerList(patternId);
                if (whichCategory === 2) setDropdownList(data);
                setLayerList(data);
              }}
            />
          )}
          {conditionalPattern && (
            <Dropdown
              category="rarity"
              categorySubType="base image"
              dropdownList={patternList}
              dropdownHeader={
                conditionalPattern ? conditionalPattern : "Select a Base Image"
              }
              handleChange={async (name, patternId) => {
                const data = await getLayerList(patternId);
                if (whichCategory === 2) setDropdownList(data);
                setLayerList(data);
              }}
            />
          )}
        </div>
      );
    }
  };

  const conditionalLayerList = () => {
    const whichCategory = category.indexOf(form.rarity_type);
    if (whichCategory > 2 && whichCategory < 6) {
      return (
        <div className="row col-sm-12">
          <div className="form-labels mt-4">Layer</div>
          {isNew && (
            <Dropdown
              category="rarity"
              categorySubType="layer"
              dropdownList={layerList}
              dropdownHeader="Select a Layer"
              handleChange={async (name, layerId) => {
                const data = await getLayerChoiceList(layerId);
                if (whichCategory === 3) setDropdownList(data);
                setLayerChoiceList(data);
              }}
            />
          )}
          {conditionalLayer && (
            <Dropdown
              category="rarity"
              categorySubType="layer"
              dropdownList={layerList}
              dropdownHeader={
                conditionalLayer ? conditionalLayer : "Select a Layer"
              }
              handleChange={async (name, layerId) => {
                const data = await getLayerChoiceList(layerId);
                if (whichCategory === 3) setDropdownList(data);
                setLayerChoiceList(data);
              }}
            />
          )}
        </div>
      );
    }
  };

  const conditionalLayerChoiceList = () => {
    const whichCategory = category.indexOf(form.rarity_type);
    if (whichCategory > 3 && whichCategory < 6) {
      return (
        <Dropdown
          category="rarity"
          categorySubType="layerChoice"
          dropdownList={layerChoiceList}
          dropdownHeader="Select a LayerChoice"
          handleChange={async (name, layerId) => {
            const data = await getLayerChoiceList(layerId);
            if (whichCategory === 2) setDropdownList(data);
            setLayerList(data);
          }}
        />
      );
    }
  };

  const showPreview = () => {
    return (
      <img
        src={form.name} // Display preview image for layer choice
        width={30}
        height={30}
        alt={form.name}
      />
    );
  };

  const showColor = () => {
    return (
      <div className="color-box" style={{ backgroundColor: form.name }}></div>
    )
  }

  const dynamicDropdownList = () => {
    if (form.rarity_type) {
      return (
        <>
          <div className="row col-sm-12 mt-5">
            <div
              className="form-labels"
            >{`${form.rarity_type
              .charAt(0)
              .toUpperCase()}${form.rarity_type.slice(1)}`}</div>
            <Dropdown
              category="rarity"
              categorySubType={form.rarity_type}
              dropdownList={dropdownList}
              handleChange={handleDropdownChange}
              dropdownHeader={
                (form.rarity_type === "layer" && form.name)
                  ? form.name
                  : (form.rarity_type === "background" && form.name)
                  ? showColor
                  : (form.rarity_type === "base image" && form.name) ||
                    (form.rarity_type === "layerchoice" && form.name)
                  ? showPreview
                  : "Select an option"
              }
            />
          </div>
        </>
      );
    }
  };

  return (
    <>
      <div className="col-sm-12 ps-4">
        <div className="row col-sm-12 mt-4">
          <div className="form-labels">
            Rarity for
          </div>
          {/*------- Web Main Color -------*/}
          <div className="col-sm-12">
            <Dropdown
              category="rarity"
              categorySubType="main"
              dropdownList={category}
              handleChange={handleDropdownChange}
              dropdownHeader={
                form.rarity_type ? form.rarity_type : "Select a Category"
              }
            />
            {errorRarityType ? (
              <span id="errorRarityType" className="form-text text-danger">
                Rarity is required
              </span>
            ) : (
              <span></span>
            )}
          </div>
        </div>
        {conditionalPatternList()}
        {conditionalLayerList()}
        {conditionalLayerChoiceList()}
        {dynamicDropdownList()}

        <div className="row col-sm-12 mt-5">
          {/* starts on */}
          <div className="col-sm-6">
            <div className="form-labels">
              Starts On{" "}
            </div>
            <DatePicker
              className="form-control inputs"
              selected={startDate}
              minDate={new Date()}
              dateFormat="yyyy/MM/dd"
              onChange={(date) => {
                setForm({ ...form, starts_on: convertLocalToUTCDate(date) });
                setStartDate(date);
              }}
            />
            {errorStartsOn ? (
              <span id="errorStartsOn" className="form-text text-danger">
                Starts On is required
              </span>
            ) : (
              <span></span>
            )}
          </div>

          {/* ends on */}
          <div className="col-sm-6">
            <div className="form-labels">
              Ends On{" "}
            </div>
            <DatePicker
              className="form-control inputs"
              selected={endDate}
              dateFormat="yyyy/MM/dd"
              minDate={
                startDate
                  ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000)
                  : new Date()
              }
              onChange={(date) => {
                setForm({ ...form, ends_on: convertLocalToUTCDate(date) });
                setEndDate(date);
              }}
            />
            {errorEndsOn ? (
              <span id="errorEndsOn" className="form-text text-danger">
                Ends On is required
              </span>
            ) : (
              <span></span>
            )}
          </div>
        </div>

        {/* Url */}
        <div className="col-sm-12 mt-5 pe-4">
          <div className="form-labels">
            Maximum Collection Size
          </div>

          <input
            type="number"
            name="max_count"
            id="max_count"
            placeholder=""
            onChange={handleChange}
            value={form.max_count}
            className="form-control inputs"
          />
          {errorMaxCount ? (
            <span id="errorStartsOn" className="form-text text-danger">
              Max Collection Size is required
            </span>
          ) : (
            <span></span>
          )}
        </div>

        <div className="pt-5">
          <div className="btn btn-secondary me-4 form-btn" onClick={onDiscard}>
            Discard
          </div>
          <input
            type="button"
            value="Save Changes"
            className="btn bg-orange form-btn"
            onClick={handleClick}
            disabled={hasUnsavedChanges}
          />
        </div>
      </div>
    </>
  );
};

export default Form;
